import React, { useState, useEffect } from 'react';
import translate from '../../i18n/translate';
import { LOCALES } from '../../i18n';

const DAPP_URL = 'https://sepolia.metha.life/';
const DOCS_URL = 'https://docs.metha.life/';

const LANGS = [
  { id: LOCALES.ENGLISH, label: 'EN' },
  { id: LOCALES.SPANISH, label: 'ES' },
  { id: LOCALES.FRENCH,  label: 'FR' },
];

export default function Nav({ theme, onToggleTheme, locale, onChangeLocale }) {
  const themeLabel = theme === 'dark' ? 'DARK' : 'LIGHT';

  // Drawer state for mobile
  const [mobileOpen, setMobileOpen] = useState(false);
  // Track scroll to fade-in the nav background only after scrolling past hero top
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // (No body-scroll lock — it was blocking scrollIntoView when closing
  //  the drawer + navigating to an anchor in the same frame.)

  const onAnchor = (e, id) => {
    e.preventDefault();
    // Close drawer first; defer scroll one frame so React + the closing
    // transition can release any layout/scroll trapping.
    setMobileOpen(false);
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const navClass =
    'lv2-nav' +
    (scrolled    ? ' lv2-nav--scrolled' : ' lv2-nav--top') +
    (mobileOpen  ? ' lv2-nav--open'     : '');

  return (
    <>
    <header className={navClass}>
      <div className="lv2-shell lv2-nav-inner">
        <a href="#top" className="lv2-logo" onClick={(e) => onAnchor(e, 'top')}>
          <img
            className="lv2-logo-img"
            src="/images/logo.png"
            srcSet="/images/logo.png 1x, /images/logo2x.png 2x"
            alt="Metha Biofund"
          />
        </a>

        {/* Desktop nav links */}
        <nav className="lv2-nav-links">
          <a href="#top"      onClick={(e) => onAnchor(e, 'top')}>{translate('lv2-nav-protocol')}</a>
          <a href="#vision"   onClick={(e) => onAnchor(e, 'vision')}>{translate('lv2-nav-vision')}</a>
          <a href="#benefits" onClick={(e) => onAnchor(e, 'benefits')}>{translate('lv2-nav-benefits')}</a>
          <a href="#papers"   onClick={(e) => onAnchor(e, 'papers')}>{translate('lv2-nav-papers')}</a>
          <a href={DOCS_URL} target="_blank" rel="noopener noreferrer">
            {translate('lv2-nav-docs')} <span style={{ fontFamily: 'var(--mono)' }}>↗</span>
          </a>
          <a href="#faq"      onClick={(e) => onAnchor(e, 'faq')}>{translate('lv2-nav-faq')}</a>
        </nav>

        {/* Desktop actions */}
        <div className="lv2-nav-actions">
          <button
            type="button"
            className="lv2-theme-toggle"
            onClick={onToggleTheme}
            title="Toggle theme"
          >
            <span className="dot" />
            <span>{themeLabel}</span>
          </button>

          <div className="lv2-lang">
            {LANGS.map(l => (
              <button
                key={l.id}
                type="button"
                className={l.id === locale ? 'active' : ''}
                onClick={() => onChangeLocale(l.id)}
              >
                {l.label}
              </button>
            ))}
          </div>

          <a
            className="lv2-btn lv2-btn-prim"
            href={DAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {translate('lv2-nav-cta-dapp')} <span style={{ fontFamily: 'var(--mono)' }}>↗</span>
          </a>
        </div>

        {/* Mobile hamburger — only visible below 880px */}
        <button
          type="button"
          className={'lv2-burger' + (mobileOpen ? ' is-open' : '')}
          aria-label="Toggle menu"
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen(o => !o)}
        >
          <span className={'lv2-burger-bar' + (mobileOpen ? ' is-x1' : '')} />
          <span className={'lv2-burger-bar' + (mobileOpen ? ' is-x2' : '')} />
          <span className={'lv2-burger-bar' + (mobileOpen ? ' is-x3' : '')} />
        </button>
      </div>
    </header>

    {/* Backdrop + Drawer rendered OUTSIDE <header> so backdrop-filter on the
        nav doesn't establish a containing block that would trap position:fixed. */}
    <div
      className={'lv2-drawer-backdrop' + (mobileOpen ? ' is-open' : '')}
      onClick={() => setMobileOpen(false)}
      aria-hidden="true"
    />
    <aside className={'lv2-drawer' + (mobileOpen ? ' is-open' : '')} aria-hidden={!mobileOpen}>
      <div className="lv2-drawer-inner">
        <nav className="lv2-drawer-links">
          <a href="#top"      onClick={(e) => onAnchor(e, 'top')}>{translate('lv2-nav-protocol')}</a>
          <a href="#vision"   onClick={(e) => onAnchor(e, 'vision')}>{translate('lv2-nav-vision')}</a>
          <a href="#benefits" onClick={(e) => onAnchor(e, 'benefits')}>{translate('lv2-nav-benefits')}</a>
          <a href="#papers"   onClick={(e) => onAnchor(e, 'papers')}>{translate('lv2-nav-papers')}</a>
          <a
            href={DOCS_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setMobileOpen(false)}
          >
            {translate('lv2-nav-docs')} <span style={{ fontFamily: 'var(--mono)' }}>↗</span>
          </a>
          <a href="#faq"      onClick={(e) => onAnchor(e, 'faq')}>{translate('lv2-nav-faq')}</a>
        </nav>

        <div className="lv2-drawer-meta">
          <button
            type="button"
            className="lv2-theme-toggle"
            onClick={onToggleTheme}
          >
            <span className="dot" />
            <span>{themeLabel}</span>
          </button>

          <div className="lv2-lang">
            {LANGS.map(l => (
              <button
                key={l.id}
                type="button"
                className={l.id === locale ? 'active' : ''}
                onClick={() => onChangeLocale(l.id)}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>

        <a
          className="lv2-btn lv2-btn-prim lv2-drawer-cta"
          href={DAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setMobileOpen(false)}
        >
          {translate('lv2-nav-cta-dapp')} <span style={{ fontFamily: 'var(--mono)' }}>↗</span>
        </a>
      </div>
    </aside>
    </>
  );
}
